/**
 * Multi-day / multi-session schedule on a quotation.
 * Kept in step with server/quoteSessions.js.
 */

import { formatPackageDuration } from './packageDuration';
import { formatPreferredDate } from './preferredDate';
import { ShootSummaryInput, shootPlace, shootSummaryLines } from './shootSummary';

export const ADHOC_PACKAGE_ID = '__adhoc__';
export const MAX_QUOTE_SESSIONS = 14;

export interface QuoteSessionLocation {
  name?: string;
  address?: string;
  mapsUrl?: string;
}

/** One dated coverage block on a quotation or booking. */
export interface QuoteSession {
  id: string;
  /** e.g. "Day 1 - Ceremony". */
  label?: string;
  /** YYYY-MM-DD, or '' when still to be arranged. */
  date?: string;
  /** HH:MM, or '' when there is no preference. */
  startTime?: string;
  durationMinutes?: number;
  location?: QuoteSessionLocation | null;
  /** Major-unit price for this session; used only on ad hoc quotes. */
  priceMajor?: number | null;
  notes?: string;
}

export interface AdhocPackage {
  name: string;
  description?: string;
  features?: string[];
  currency: string;
}

export function isAdhocPackageId(packageId?: string | null): boolean {
  return String(packageId || '') === ADHOC_PACKAGE_ID;
}

export function newSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function blankSession(partial?: Partial<QuoteSession>): QuoteSession {
  return {
    id: partial?.id || newSessionId(),
    label: partial?.label || '',
    date: partial?.date || '',
    startTime: partial?.startTime || '',
    durationMinutes: Number(partial?.durationMinutes) > 0 ? Math.round(Number(partial?.durationMinutes)) : 180,
    location: partial?.location || null,
    priceMajor: partial?.priceMajor ?? null,
    notes: partial?.notes || '',
  };
}

/** Old quotes with only preferredDate/time become one implied session. */
export function sessionsFromQuote(quote?: {
  sessions?: QuoteSession[] | null;
  preferredDate?: string;
  preferredTime?: string;
  preferredLocation?: QuoteSessionLocation | null;
  snapshot?: { lines?: Array<{ kind?: string; durationMinutes?: number; sessionId?: string }> } | null;
  adhocPackage?: AdhocPackage | null;
} | null): QuoteSession[] {
  if (!quote) return [];
  if (Array.isArray(quote.sessions) && quote.sessions.length > 0) {
    return quote.sessions.map((session) => ({
      id: String(session.id || newSessionId()),
      label: String(session.label || ''),
      date: String(session.date || ''),
      startTime: String(session.startTime || ''),
      durationMinutes: Number(session.durationMinutes) > 0 ? Math.round(Number(session.durationMinutes)) : 0,
      location: session.location || null,
      priceMajor: session.priceMajor == null ? null : Number(session.priceMajor),
      notes: String(session.notes || ''),
    }));
  }
  const duration = (() => {
    const lines = quote.snapshot?.lines || [];
    const packageLine = lines.find((item) => item?.kind === 'package' && Number(item.durationMinutes) > 0);
    if (packageLine) return Math.round(Number(packageLine.durationMinutes));
    const sessionMinutes = lines
      .filter((item) => item?.kind === 'session')
      .reduce((sum, item) => sum + (Number(item.durationMinutes) > 0 ? Math.round(Number(item.durationMinutes)) : 0), 0);
    return sessionMinutes;
  })();
  if (!quote.preferredDate && !quote.preferredTime && !duration && !quote.adhocPackage) return [];
  return [
    blankSession({
      id: 'session-1',
      label: '',
      date: quote.preferredDate || '',
      startTime: quote.preferredTime || '',
      durationMinutes: duration || 0,
      location: quote.preferredLocation || null,
    }),
  ];
}

/** Mirror the first session into the legacy preferred fields. */
export function preferredFromSessions(sessions: QuoteSession[]): {
  preferredDate: string;
  preferredTime: string;
  preferredLocation: QuoteSessionLocation;
} {
  const first = sessions[0];
  if (!first) {
    return { preferredDate: '', preferredTime: '', preferredLocation: { name: '', address: '', mapsUrl: '' } };
  }
  const location = first.location || { name: '', address: '', mapsUrl: '' };
  return {
    preferredDate: first.date || '',
    preferredTime: first.startTime || '',
    preferredLocation: {
      name: location.name || '',
      address: location.address || '',
      mapsUrl: location.mapsUrl || '',
    },
  };
}

export function totalSessionMinutes(sessions: QuoteSession[]): number {
  return sessions.reduce((sum, session) => {
    const minutes = Number(session.durationMinutes);
    return sum + (Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : 0);
  }, 0);
}

function endClock(startTime?: string, durationMinutes?: number): string {
  const match = /^(\d{2}):(\d{2})$/.exec(String(startTime || ''));
  const minutes = Number(durationMinutes);
  if (!match || !Number.isFinite(minutes) || minutes <= 0) return '';
  const total = Number(match[1]) * 60 + Number(match[2]) + Math.round(minutes);
  const wrapped = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

/** One line describing a session for documents and messages. */
export function formatSessionLine(
  session: QuoteSession,
  options: { index?: number; total?: number; fallbackLocation?: QuoteSessionLocation | null } = {},
): string {
  const label = String(session.label || '').trim()
    || (options.total && options.total > 1 && options.index != null
      ? `Day ${options.index + 1}`
      : '');
  const date = formatPreferredDate(session.date) || 'date to be arranged';
  const start = session.startTime || '';
  const end = endClock(session.startTime, session.durationMinutes);
  const time = start ? (end ? `${start}-${end}` : start) : 'time to be agreed';
  const coverage = formatPackageDuration(session.durationMinutes);
  const place = shootPlace(session.location || options.fallbackLocation || null);
  const parts = [
    label ? `${label}:` : '',
    date,
    time,
    coverage ? `(${coverage})` : '',
    place ? `· ${place}` : '',
  ].filter(Boolean);
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Schedule lines for client documents.
 * One session keeps today's "Preferred shoot date / Start time / Duration / Location" wording.
 * Several sessions list each day, then total coverage.
 */
export function sessionSummaryLines(
  sessions: QuoteSession[],
  options: {
    confirmed?: boolean;
    fallbackLocation?: QuoteSessionLocation | null;
    /** When there is only one session and no explicit list, fall back to the classic shoot block. */
    singleShoot?: ShootSummaryInput;
  } = {},
): string[] {
  const rows = sessions.filter(Boolean);
  if (rows.length <= 1) {
    const only = rows[0];
    if (options.singleShoot) return shootSummaryLines(options.singleShoot);
    if (!only) return [];
    return shootSummaryLines({
      date: only.date || '',
      time: only.startTime || '',
      location: only.location || options.fallbackLocation || null,
      confirmed: Boolean(options.confirmed),
      durationMinutes: only.durationMinutes || undefined,
    });
  }

  const heading = options.confirmed ? 'Schedule:' : 'Preferred schedule:';
  const lines = [heading];
  rows.forEach((session, index) => {
    lines.push(formatSessionLine(session, {
      index,
      total: rows.length,
      fallbackLocation: options.fallbackLocation,
    }));
  });
  const total = formatPackageDuration(totalSessionMinutes(rows));
  if (total) lines.push(`Total coverage: ${total}`);
  return lines;
}

/** Last session date on a schedule (YYYY-MM-DD), or ''. */
export function lastSessionDate(sessions: QuoteSession[]): string {
  return sessions
    .map((session) => String(session.date || ''))
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort()
    .at(-1) || '';
}
